<template>
  <section
    class="bg-gray-100 border-gray-200 shadow border p-4 rounded-sm flex flex-col gap-2 fixed top-14 right-0 bottom-0 text-sm w-[250px]"
  >
    <SMInput v-if="!(isInsideZoneLayout && app.currentSection)" v-model="seatmapNameValue" name="seatmapName" />

    <h1 v-if="isInsideZoneLayout && app.currentSection" class="font-bold text-lg">
      Section {{ app.currentSection.label?.text() }}
    </h1>

    <div v-if="isInsideZoneLayout && selectedUnits.length > 0">
      <strong>{{ selectedUnits.length - invisibleSelectedSeats.length }}</strong>
      selected
      {{ invisibleSeats > 0 ? `(${selectedUnits.length} total)` : '' }}
    </div>

    <div v-if="!isInsideZoneLayout && totalZones > 0" class="grouped-sections">
      <h2 class="text-base">Sections</h2>
      <div v-if="!selectedSections.length">
        <strong>{{ totalZones }}</strong>
        sections
      </div>
      <div v-else>
        <strong>{{ selectedSections.length }}</strong>
        selected ({{ totalZones }} total)
      </div>
      <div v-if="unassignedSectionCnt" class="text-red-600">
        <strong>{{ unassignedSectionCnt }}</strong>
        uncategorized sections
      </div>
    </div>

    <div v-if="totalSeats > 0">
      <h2 class="text-base">Seats</h2>
      <div v-if="rowSeats > 0">
        <strong>{{ rowSeats }}</strong>
        row seats
      </div>
      <div v-if="tableSeats > 0">
        <b>{{ tableSeats }}</b>
        table seats in
        <b>{{ tablesCount }}</b>
        tables
      </div>
      <div v-if="gaSeats > 0">
        <strong>{{ gaSeats }}</strong>
        GA seats in
        <strong>{{ gaCount }}</strong>
        GA
      </div>
      <div v-if="totalSeats > 0">
        <strong>{{ totalSeats }}</strong>
        total seats
      </div>
      <div v-if="disabledSeats > 0">
        <strong>{{ disabledSeats }}</strong>
        disabled seats
      </div>
      <div v-if="invisibleSeats > 0">
        <strong>{{ invisibleSeats }}</strong>
        invisible seats
      </div>
      <div v-if="blockedSeats > 0 && showLocksOnSeatsValue">
        <strong>{{ blockedSeats }}</strong>
        blocked seats
        <div v-if="$app.props.holdTypes?.length" class="grid gap-x-1 ml-2" style="grid-template-columns: auto 1fr">
          <template v-if="seatsByHoldType['__default']">
            <div>{{ seatsByHoldType['__default'] }}</div>
            <div class="flex gap-1">
              <span>Blocked</span>
            </div>
          </template>
          <template v-for="holdType in $app.props.holdTypes" :key="holdType.key">
            <template v-if="seatsByHoldType[holdType.key]">
              <div>{{ seatsByHoldType[holdType.key] }}</div>
              <div class="flex gap-1">
                <span>{{ holdType.value }}</span>
                <strong>({{ holdType.key }})</strong>
              </div>
            </template>
          </template>
        </div>
      </div>
      <div v-if="blockedGa > 0 && showLocksOnSeatsValue" class="text-orange-600">
        <strong>{{ blockedGa }}</strong>
        blocked GA
      </div>
    </div>

    <Labels :is-labels-mode="isLabelsMode"></Labels>
    <Categories :is-categories-mode="isCategoriesMode"></Categories>
    <Pos :is-pos-mode="isPosMode"></Pos>

    <div class="grouped-switches">
      <SMSwitch v-if="!isLabelsMode" v-model="showLabelsValue">Show labels</SMSwitch>
      <SMSwitch v-if="!isBlockSeatsMode" v-model="showLocksOnSeatsValue">Show blocked</SMSwitch>
      <SMSwitch v-if="!isInsideZoneLayout" id="show-grid" v-model="showGridValue" name="showGrid">Show grid</SMSwitch>
      <SMSwitch v-model="snapToGridValue">Snap to grid</SMSwitch>
    </div>

    <div v-if="isInsideZoneLayout">
      <span>Scale Section</span>
      <SMSlider
        :value="scale * 100"
        tooltip
        :min="scaleZone.min"
        :max="scaleZone.max"
        :interval="scaleZone.interval"
        :debounce="300"
        @update:modelValue="onScaleZoneValueChange"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, shallowRef, watch } from 'vue';
import { SEAT, SEATSTATUS, TYPE } from '@/constants';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import sumBy from 'lodash/sumBy';
import eventbus from 'tiny-emitter/instance';
import Categories from './categories.vue';
import Application from '@/core/application';
import { Unit } from '@/core/models/index';
import { GA } from '@/core/models/ga/ga';
import { Seat } from '@/core/models/seat';
import Selection from '@/core/models/selection';
import { TableSeat } from '@/core/models/tables/tableSeat';
import { useActions, useGetters, useMutations, useState } from '@/store/useStore';
import SMSlider from '../ui/SMSlider.vue';
import Labels from './labels.vue';
import Pos from './pos.vue';
import SMIcon from '../ui/SMIcon.vue';
import SMInput from '../ui/SMInput.vue';
import SMSwitch from '../ui/legacy/SMSwitch.vue';
import { Section } from '@/core/models/sections/section';

const app = Application.instance;
const collapsed = ref(false);
const selectedUnits = shallowRef<Unit[]>(app.seatmap.selection.items);
const totalZones = ref(0);

const invisibleSelectedSeats = computed(() => {
  return selectedUnits.value.filter((seat) => seat.attributes.invisible == true);
});

const selectedSections = computed(() => {
  return selectedUnits.value.filter((section) => section.type === TYPE.SECTION) as Section[];
});

const selectedSectionUuids = computed(() => {
  return selectedSections.value.map((section) => section.uuid);
});

const scaleZone = reactive({
  tooltip: 'hover',
  width: 400,
  min: 10,
  max: 300,
  interval: 1,
});

const unassignedSectionCnt = ref(app.unassignedSections.length);

const seatsByHoldType = shallowRef<{ [key: string]: number }>({});

eventbus.on('refreshUnassignedCount', () => {
  unassignedSectionCnt.value = app.unassignedSections.length;
});

eventbus.on('seatmap:updated', () => {
  unassignedSectionCnt.value = app.unassignedSections.length;
});

const { tablesCount, tableSeats, gaCount, gaSeats, blockedSeats, blockedGa, invisibleSeats, disabledSeats } = useState('stats');
const { layout, shiftKey } = useState('settings');
const { showGrid, snapToGrid, seatmapName, scale } = useState('camera');
const { showLabels, showLocks, showHiddenSeats } = useState('seatmap');

const { isInsideZoneLayout, isPosMode, isCategoriesMode, isLabelsMode, isBlockSeatsMode } = useGetters('settings');

const { totalSeats, rowSeats } = useGetters('stats');

const {
  changeRowSeats,
  changeTablesCount,
  changeTableSeats,
  changeGASeats,
  changeGACount,
  changeBlockedSeats,
  changeInvisibleSeats,
  changeDisabledSeats,
  changeBlockedGa,
  changeUnlabeledSeats,
} = useActions('stats');

const { setGrid, setSnapToGrid, setSeatmapName } = useActions('camera');
const { setShowLabels, setShowLocks, setShowHiddenSeats } = useActions('seatmap');
const { updateUnsavedChanges } = useMutations('seatmap');
const { setScale } = useMutations('camera');

const snapToGridValue = computed({
  get() {
    return snapToGrid.value;
  },
  set(flag) {
    setSnapToGrid(flag);
    updateUnsavedChanges(true);
  },
});

const showGridValue = computed({
  get() {
    return showGrid.value;
  },
  set(flag) {
    setGrid(flag);
    updateUnsavedChanges(true);
  },
});

// eslint-disable-next-line no-unused-vars
const showHiddenSeatsValue = computed({
  get() {
    return showHiddenSeats.value;
  },
  set(flag) {
    setShowHiddenSeats(flag);
    updateUnsavedChanges(true);
  },
});

const showLabelsValue = computed({
  get() {
    return showLabels.value;
  },
  set(flag) {
    setShowLabels(flag);
    updateUnsavedChanges(true);
  },
});

const showLocksOnSeatsValue = computed({
  get() {
    return showLocks.value;
  },
  set(flag) {
    setShowLocks(flag);
    updateUnsavedChanges(true);
  },
});

const seatmapNameValue = computed({
  get() {
    return seatmapName.value;
  },
  set(name) {
    setSeatmapName(name);
    updateUnsavedChanges(true);
  },
});

watch(
  layout,
  () => {
    onSeatmapUpdate(app.seatmap.seats, app.seatmap.units);
  },
  { immediate: true }
);

watch(
  shiftKey,
  (val) => {
    console.log('shiftKey', val && selectedUnits.value.length > 0);
    if (val && selectedUnits.value.length > 0) {
      onSeatmapUpdate(app.seatmap.seats, app.seatmap.units);
    }
  },
  { immediate: true }
);

// Methods
function recalculateZones() {
  totalZones.value = app.seatmap.sections.length;
}

recalculateZones();

function seatAndItsRowHaveLabels(seat: Seat | TableSeat) {
  return (
    seat.attributes.label !== SEAT.LABEL &&
    seat.attributes.label !== '' &&
    // @ts-expect-error - row OR table will be defined
    (seat.row || seat.table).attributes.label !== SEAT.LABEL &&
    // @ts-expect-error
    (seat.row || seat.table).attributes.label !== ''
  );
}

function updateUnlabeledSeatsCount() {
  const seatsAndTableSeats: Array<Seat | TableSeat> = app.seatmap.getVisibleSeats(app.currentSection?.uuid);
  const labeledSeats = seatsAndTableSeats.filter((s) => seatAndItsRowHaveLabels(s));
  changeUnlabeledSeats(seatsAndTableSeats.length - labeledSeats.length);
}

function getFilteredSeats(
  sid: string | null = null,
  cond?: (seat: Seat | TableSeat) => boolean,
  seatsAndTableSeats = app.seatmap.seatsAndTableSeats
): Array<Seat | TableSeat> {
  if (sid) {
    seatsAndTableSeats = filter(seatsAndTableSeats, (seat) => ('table' in seat ? seat.table.sid : seat.sid) === sid);
  }
  return cond ? filter(seatsAndTableSeats, cond) : seatsAndTableSeats;
}

function getFilteredSeatsMulti(
  sids: string[],
  cond?: (seat: Seat | TableSeat) => boolean,
  seatsAndTableSeats = app.seatmap.seatsAndTableSeats
): Array<Seat | TableSeat> {
  if (sids.length) {
    seatsAndTableSeats = filter(seatsAndTableSeats, (seat) =>
      sids.includes(('table' in seat ? seat.table.sid : seat.sid)!)
    );
  }
  return cond ? filter(seatsAndTableSeats, cond) : seatsAndTableSeats;
}

function updateHolds(blockedSeats: (Seat | TableSeat)[]) {
  const holds: { [key: string]: number } = {};
  blockedSeats.forEach((seat) => {
    if (seat.attributes.invisible === true) return;
    if (seat.attributes.holdType) {
      holds[seat.attributes.holdType] = holds[seat.attributes.holdType] ? holds[seat.attributes.holdType] + 1 : 1;
    } else {
      holds['__default'] = holds['__default'] ? holds['__default'] + 1 : 1;
    }
  });

  seatsByHoldType.value = holds;
}

function updateBlockedSeatsCount(seatsAndTableSeats?: (Seat | TableSeat)[]) {
  const cond = (seat: Seat | TableSeat) => seat.attributes.invisible !== true && seat.locked;
  if (!isInsideZoneLayout.value && selectedSections.value.length) {
    seatsAndTableSeats = getFilteredSeatsMulti(selectedSectionUuids.value, cond, seatsAndTableSeats);
  } else {
    seatsAndTableSeats = getFilteredSeats(app.currentSection?.uuid, cond, seatsAndTableSeats);
  }

  // const seatsAndTableSeats = isInsideZoneLayout ? app.seatmap.getVisibleSeats(app.currentSection?.uuid) :
  // const blockedSeats1 = filter(seatsAndTableSeats, (fs) => fs.locked);
  changeBlockedSeats(seatsAndTableSeats.length);

  updateHolds(seatsAndTableSeats);
}

function updateGaBlocked() {
  let blockedGa = 0;
  const gaSeats: GA[] = app.getFilteredGa(isInsideZoneLayout.value);
  blockedGa = sumBy(gaSeats, (g) => g.attributes.blocked);
  changeBlockedGa(blockedGa);
}


function updateInvisibleSeatsCount() {
  let seatsAndTableSeats: (Seat | TableSeat)[];

  const cond = (seat: Seat | TableSeat) => seat.attributes.invisible === true;
  if (!isInsideZoneLayout.value && selectedSections.value.length) {
    seatsAndTableSeats = getFilteredSeatsMulti(selectedSectionUuids.value, cond);
  } else {
    seatsAndTableSeats = getFilteredSeats(app.currentSection?.uuid, cond);
  }

  changeInvisibleSeats(seatsAndTableSeats.length);

  // const seatsAndTableSeats = app
  //   .getFilteredSeats(app.currentSection?.uuid)
  //   .filter((s) => s.attributes.invisible === true);
  // changeInvisibleSeats(seatsAndTableSeats.length);
}

function updateDisabledSeatsCount() {
  let seatsAndTableSeats: (Seat | TableSeat)[];

  const cond = (seat: Seat | TableSeat) => seat.attributes.status === SEATSTATUS.DISABLED;
  if (!isInsideZoneLayout.value && selectedSections.value.length) {
    seatsAndTableSeats = getFilteredSeatsMulti(selectedSectionUuids.value, cond);
  } else {
    seatsAndTableSeats = getFilteredSeats(app.currentSection?.uuid, cond);
  }

  changeDisabledSeats(seatsAndTableSeats.length);

  // const seatsAndTableSeats = app
  //   .getFilteredSeats(app.currentSection?.uuid)
  //   .filter((s) => s.attributes.invisible === true);
  // changeInvisibleSeats(seatsAndTableSeats.length);
}

function onSelectionUpdate(selection: Selection) {
  selectedUnits.value = selection.items;
}

function onSeatmapUpdate(seats: Seat[], units: Array<Unit>) {
  let filteredUnits = units;
  let filteredSeats = seats;
  let tablesCount = 0;
  let tableSeats = 0;
  let gaCount = 0;
  let gaSeats = 0;

  if (isInsideZoneLayout.value) {
    filteredSeats = filter(seats, (seat) => seat.sid === app.currentSection?.uuid);
    filteredUnits = filter(units, (unit) => unit.sid === app.currentSection?.uuid);
  } else if (shiftKey.value && selectedUnits.value.length > 0) {
    filteredSeats = filter(seats, (seat) => selectedSectionUuids.value.includes(seat.sid!));
    filteredUnits = filter(units, (unit) => selectedSectionUuids.value.includes(unit.sid!));
  }

  changeRowSeats(filteredSeats.length);
  forEach(filteredUnits, (unit) => {
    switch (unit.type) {
      case TYPE.TABLE:
        tablesCount += 1;
        tableSeats += unit.seatsCount;
        break;
      case TYPE.GA:
        gaCount += 1;
        gaSeats += unit.seatsCount;
        break;
      default:
        break;
    }
  });
  changeTablesCount(tablesCount);
  changeTableSeats(tableSeats);
  changeGACount(gaCount);
  changeGASeats(gaSeats);
  updateBlockedSeatsCount(filteredSeats);
  updateUnlabeledSeatsCount();
}

// function getCurrentZoneScale() {
//   let currentZoneScale = 100;
//   if (app.seatmap.sections) {
//     forEach(app.seatmap.sections, (section) => {
//       if (currentZoneScale > section.attributes.scale) {
//         currentZoneScale = parseFloat(section.attributes.scale, 10) * 100;
//       }
//     });
//   }
//   return currentZoneScale;
// }

function onScaleZoneValueChange(value: number) {
  setScale(value / 100);
  // forEach(app.seatmap.sections, (section) => {
  //   section.changeScale(value, oldScaleZoneValue.value);
  // });
  // oldScaleZoneValue.value = Math.abs(value);
  // eventbus.emit('selection:delayedHistory', 'Section scale change');
}

// Event listeners
eventbus.on('selection:updated', onSelectionUpdate);
eventbus.on('seatmap:updated', onSeatmapUpdate);
eventbus.on('seats:lock:updated', updateBlockedSeatsCount);
eventbus.on('ga:block:updated', updateGaBlocked);
eventbus.on('seatmap:updated', updateGaBlocked);
eventbus.on('seatmap:updated', recalculateZones);
eventbus.on('update:invisibleSeats', updateInvisibleSeatsCount);
eventbus.on('layout:changed', updateInvisibleSeatsCount);
eventbus.on('selection:clone', updateInvisibleSeatsCount);
eventbus.on('seats:disabled:updated', updateDisabledSeatsCount);
eventbus.on('layout:changed', updateDisabledSeatsCount);
eventbus.on('selection:clone', updateDisabledSeatsCount);

updateInvisibleSeatsCount();
updateDisabledSeatsCount()

onBeforeUnmount(() => {
  eventbus.off('selection:updated', onSelectionUpdate);
  eventbus.off('seatmap:updated', onSeatmapUpdate);
  eventbus.off('seats:lock:updated', updateBlockedSeatsCount);
  eventbus.off('ga:block:updated', updateGaBlocked);
  eventbus.off('seatmap:updated', updateGaBlocked);
  eventbus.off('seatmap:updated', recalculateZones);
  eventbus.off('update:invisibleSeats', updateInvisibleSeatsCount);
  eventbus.off('layout:changed', updateInvisibleSeatsCount);
  eventbus.off('selection:clone', updateInvisibleSeatsCount);
});
</script>
